import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowRight, Music, Users, Heart, Calendar } from 'lucide-react';

export const HomePage = () => {
  const heroImage = "https://images.unsplash.com/photo-1637345877917-a3bd513e8078?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHx0YW5nbyUyMGRhbmNlJTIwY291cGxlJTIwZHJhbWF0aWMlMjBsaWdodGluZ3xlbnwwfHx8fDE3NzIzNTkzMDV8MA&ixlib=rb-4.1.0&q=85";
  const socialImage = "https://images.unsplash.com/photo-1767784097269-943e5c8bd3b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxzb2NpYWwlMjBkYW5jZSUyMHBhcnR5JTIwZWxlZ2FudHxlbnwwfHx8fDE3NzIzNTkzMTZ8MA&ixlib=rb-4.1.0&q=85";

  const features = [
    {
      icon: Music,
      title: 'Professionelle Kurse',
      description: 'Erfahrene Tanzlehrer führen Sie durch alle Stufen des Tango Argentino'
    },
    {
      icon: Users,
      title: 'Paar-Buchungen',
      description: 'Buchen Sie gemeinsam mit Ihrem Partner und sparen Sie dabei'
    },
    {
      icon: Heart,
      title: 'Tanzpartnerbörse',
      description: 'Finden Sie den perfekten Tanzpartner in unserer Community'
    },
    {
      icon: Calendar,
      title: 'Flexible Termine',
      description: 'Wählen Sie aus verschiedenen Kurszeiten, die zu Ihrem Leben passen'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 bg-background/40" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-widest text-primary mb-6 animate-fadeIn">
            Tango Argentino in Berlin
          </p>
          <h1 className="text-5xl md:text-7xl font-heading font-bold mb-6 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
            Die Kunst der<br />
            <span className="text-primary">Leidenschaft</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
            Entdecken Sie die Magie des Tango Argentino. 
            Professionelle Kurse für Anfänger und Fortgeschrittene 
            in einer einzigartigen Atmosphäre.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeIn" style={{ animationDelay: '0.3s' }}>
            <Link to="/courses">
              <Button className="btn-primary gap-2" data-testid="hero-courses-btn">
                Kurse entdecken
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/partner-search">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="hero-partner-btn">
                Tanzpartner finden
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-primary mb-4">
              Warum TangoFlow?
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold">
              Tanzen ist <span className="text-primary">Leidenschaft</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-8 bg-card border border-border rounded-md card-hover animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <feature.icon className="w-10 h-10 text-primary mb-6" />
                <h3 className="text-lg font-heading font-semibold mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="community-section">
        <div className="absolute inset-0 bg-gradient-to-r from-background via-card/50 to-background" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest text-primary mb-4">
                Unsere Community
              </p>
              <h2 className="text-4xl md:text-5xl font-heading font-semibold mb-6">
                Finden Sie Ihren<br />
                <span className="text-primary">Tanzpartner</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Sie haben keinen Tanzpartner? Kein Problem! In unserer 
                Tanzpartnerbörse können Sie andere Tango-Begeisterte 
                kennenlernen, die auch einen Partner für den gleichen 
                Kurs suchen. Vernetzen Sie sich, diskutieren Sie und 
                finden Sie den perfekten Partner für Ihre Tango-Reise.
              </p>
              <Link to="/partner-search">
                <Button className="btn-primary gap-2">
                  Zur Tanzpartnerbörse
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/3] rounded-md overflow-hidden">
                <img 
                  src={socialImage}
                  alt="Tango Community"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 border-2 border-primary rounded-md" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-card/50" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold mb-6">
            Bereit für den <span className="text-primary">ersten Schritt</span>?
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
            Registrieren Sie sich heute und entdecken Sie die Welt des 
            Tango Argentino. Unsere nächsten Kurse beginnen bald!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="btn-primary gap-2" size="lg" data-testid="cta-register-btn">
                Jetzt registrieren
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/courses">
              <Button variant="outline" size="lg" className="border-border">
                Alle Kurse ansehen
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
